/**
 * Client-side validation for granular LinkedIn workflow nodes (#2005, #2183).
 *
 * #2183 collapsed the composite HeyReach node to ONE step per canvas node and
 * removed the in-sequence `heyreach_delay` step type (cross-step waiting is
 * owned by the platform `delay` node). The validator therefore only needs to
 * enforce per-step content rules — the multi-step compilable-action guard
 * from the Wave 2 composite UI no longer applies (the single step IS the
 * action). Message fields are optional: a blank connection request or message
 * is AI-personalized by the HeyReach worker when the step becomes eligible.
 */

import { isNativeLinkedInSequence, validateNativeSequence } from './nativeLinkedInSequence';
import { isPhase2LinkedInStep } from './phase2TemplateSteps';

const LINKEDIN_STEP_TYPES = new Set([
  'heyreach_view_profile',
  'heyreach_like_post',
  'heyreach_follow',
  'heyreach_send_connection_request',
  'heyreach_send_message',
  'heyreach_branch_accepted',
  'heyreach_branch_replied',
  'heyreach_branch_already_connected',
  'heyreach_branch_no_recent_post',
  'heyreach_stop_on_reply',
]);

/**
 * A legacy shared-campaign pin is only real when it is a positive numeric ID.
 * 0, '0', '', and whitespace strings are artifacts of older payloads, not
 * pins — treating them as pins would let stepless nodes pass validation while
 * campaign lookup resolves nothing and sender gating silently disengages.
 */
export function parseLegacyCampaignPin(raw: unknown): string | undefined {
  if (typeof raw !== 'number' && typeof raw !== 'string') return undefined;
  const pin = Number(String(raw).trim());
  return Number.isFinite(pin) && pin > 0 ? String(pin) : undefined;
}

/**
 * Whether a LinkedIn Outreach node still has to say whose LinkedIn it sends from.
 *
 * A node with no sender used to mean "MarketBetter picks": the worker attached every LinkedIn
 * seat in the workspace to the campaign and spread the leads across all of them (Jumio,
 * 2026-10-16: a rep's campaign went out from 11 teammates' accounts). Launch is blocked until a
 * sender is chosen; mb-server refuses publish/run the same way (utils/linkedinStepSender).
 * Exempt: native sequences (held to their own one-sender contract), outreach-template steps
 * (the builder can't edit them), and a legacy pin to a HeyReach campaign, which sends from the
 * seats attached to it in HeyReach.
 */
export function needsLinkedInSender(actionType: unknown, details: Record<string, unknown> | null | undefined): boolean {
  if (actionType !== 'heyreach_campaign') return false;
  const d = details || {};
  if (isNativeLinkedInSequence(d) || isPhase2LinkedInStep(actionType, d)) return false;
  // Only a legacy add-leads node (a pin and no steps[]) sends through its pinned campaign. A node
  // with its own step gets a campaign the worker provisions with every seat, whose id it then
  // writes back here — that id is no pin.
  const hasSteps = Array.isArray(d.steps) && d.steps.length > 0;
  if (parseLegacyCampaignPin(d.campaign_id) && !hasSteps) return false;
  return linkedInSenderIdsOf(d).length === 0;
}

/**
 * Campaigns whose LinkedIn sender is decided per signal rather than per campaign (signal workflow
 * templates and their copies, system workflows); mb-server exempts the same ones.
 */
export function isLinkedInSenderExemptAudience(
  audience: { is_template?: boolean | null; is_system?: boolean | null; creation_method?: string | null } | null | undefined,
): boolean {
  return audience?.is_template === true
    || audience?.is_system === true
    || audience?.creation_method === 'gtm_signal';
}

/** The seats a LinkedIn node sends from: its pool, else the legacy single sender. */
export function linkedInSenderIdsOf(details: Record<string, unknown> | null | undefined): string[] {
  const d = details || {};
  const valid = (id: unknown) => typeof id !== 'boolean' && id !== null && id !== ''
    && Number.isInteger(Number(id)) && Number(id) > 0;
  // Same precedence as the worker: a non-empty pool wins, and junk in it is not rescued by the scalar.
  if (Array.isArray(d.linkedInAccountIds) && d.linkedInAccountIds.length > 0) {
    return [...new Set(d.linkedInAccountIds.filter(valid).map(String))];
  }
  return valid(d.linkedInAccountId) ? [String(d.linkedInAccountId)] : [];
}

/**
 * Whose LinkedIn each LinkedIn node sends from, for the launch confirmation. Seats are named from
 * the workspace's LinkedIn accounts; an unknown seat keeps its id so it is never silently dropped.
 */
export function linkedInSendersByStep(
  plays: WorkflowPlayLike[],
  accounts: Array<{ id: number | string; name?: string | null }> | undefined,
): Array<{ step: string; senders: string[] }> {
  const nameById = new Map((accounts || []).map((account) => [String(account.id), account.name || '']));
  return [...plays]
    .filter((play) => play.action_type === 'heyreach_campaign')
    .sort((a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0))
    .map((play) => ({
      step: play.title || play.name || play.play_name || 'LinkedIn',
      senders: linkedInSenderIdsOf(play.custom_action_details)
        .map((id) => nameById.get(id) || `LinkedIn sender ${id}`),
    }));
}

export interface WorkflowPlayLike {
  id?: number;
  action_type?: string;
  name?: string;
  /** Step title as the plays API returns it (the builder's node label). */
  title?: string | null;
  play_name?: string;
  sequence_order?: number;
  custom_action_details?: Record<string, unknown>;
  parent_node?: number | null;
}

/**
 * Preserve validation for legacy granular audience_plays while allowing blank
 * message copy. Unknown/deprecated `heyreach_*` actions must not silently pass
 * simply because new authoring uses the composite single-step shape.
 */
function validateGranularNodeFields(plays: WorkflowPlayLike[]): string[] {
  const errors: string[] = [];
  plays.forEach((play) => {
    const type = play.action_type;
    if (!type?.startsWith('heyreach_') || type === 'heyreach_campaign') return;
    if (!LINKEDIN_STEP_TYPES.has(type)) {
      errors.push(`"${play.name || type}" has an unknown LinkedIn action_type.`);
    }
  });
  return errors;
}

/**
 * Validate the single-step composite (#2183): each `heyreach_campaign` play
 * carries `custom_action_details.steps` of length 0 or 1. If a step is set
 * and has a known action_type it is accepted; message text is optional because
 * blank message actions use late-bound AI personalization. Unknown action_types
 * are rejected. Empty `steps` is tolerated only for legacy add-leads mode,
 * where a pinned shared campaign still drives the sequence — the builder no
 * longer offers campaign selection, and a MarketBetter-managed campaign has
 * no sequence of its own, so a stepless node without a pin delivers nothing.
 */
function validateCompositeHeyReachSteps(plays: WorkflowPlayLike[], senderExempt: boolean): string[] {
  const errors: string[] = [];

  plays.forEach((play) => {
    if (play.action_type !== 'heyreach_campaign') return;
    // A native LinkedIn sequence carries the invite and its messages in one node by design; it is
    // held to its own contract instead of the one-step rule below.
    if (isNativeLinkedInSequence(play.custom_action_details)) {
      validateNativeSequence(play.custom_action_details).forEach((problem) => {
        errors.push(`"${play.name || 'LinkedIn'}": ${problem}`);
      });
      return;
    }
    const steps = play.custom_action_details?.steps;
    if (!Array.isArray(steps) || steps.length === 0) {
      if (!parseLegacyCampaignPin(play.custom_action_details?.campaign_id)) {
        errors.push(
          `"${play.name || 'LinkedIn'}" has no LinkedIn step. Add one LinkedIn touch to this node before publishing.`,
        );
      }
      return;
    }

    if (steps.length > 1) {
      errors.push(
        `"${play.name || 'LinkedIn'}" carries ${steps.length} LinkedIn steps but only one is allowed per LinkedIn Outreach node. Split the touches across separate LinkedIn Outreach nodes (use a Delay node between them).`,
      );
      return;
    }

    const rawStep = steps[0];
    const step = (rawStep ?? {}) as Record<string, unknown>;
    const type = typeof step.action_type === 'string' ? step.action_type : '';
    if (!LINKEDIN_STEP_TYPES.has(type)) {
      errors.push(`"${play.name || 'LinkedIn'}" has an unknown step action_type.`);
      return;
    }

    if (!senderExempt && needsLinkedInSender(play.action_type, play.custom_action_details)) {
      errors.push(
        `"${play.title || play.name || 'LinkedIn'}" has no sender. Choose whose LinkedIn it sends from before launching — otherwise it would send from every LinkedIn account in the workspace.`,
      );
    }
  });

  return errors;
}

/**
 * Returns human-readable validation errors. Empty array = OK to publish.
 */
export function validateLinkedInWorkflow(
  plays: WorkflowPlayLike[],
  options: { senderExempt?: boolean } = {},
): string[] {
  return [
    ...validateGranularNodeFields(plays),
    ...validateCompositeHeyReachSteps(plays, options.senderExempt === true),
  ];
}
